import { useEffect, useMemo } from 'react';
import { Select, Checkbox } from 'FrontRoyalMaterialUiForm';
import { useFormContext } from 'FrontRoyalReactHookForm';
import * as Yup from 'yup';
import { type CoachSessionOpportunity, isPostApplicationGoalSettingSessionOpportunity } from 'TutorBotCoach';
import { humanize, titleize } from 'String';
import { type ProgramApplication } from 'ProgramApplication';
import { useGetUserInAdmin } from '../../redux';
import { UserActionType, UserManagementActionNamespace, type FormFieldProps } from '../UserManagementAction.types';
import AbstractUserManagementAction from '../AbstractUserManagementAction';

type FormFields = {
    sessionType: string;
    programApplicationId?: string;
    opportunityId?: string;
    expired: boolean;
};

const availableSessionTypes = ['post_application_goal_setting'];
const defaultSessionType = 'post_application_goal_setting';

function getProgramApplicationLabel(programApplication: ProgramApplication) {
    return `${programApplication.cohortName} - ${programApplication.status}`;
}

function getCoachSessionOpportunityLabel(
    opportunity: CoachSessionOpportunity,
    programApplications: ProgramApplication[],
) {
    const sessionTypeTitle = titleize(humanize(opportunity.sessionType));
    if (isPostApplicationGoalSettingSessionOpportunity(opportunity)) {
        const programApplication = programApplications.find(app => app.id === opportunity.programApplicationId)!;
        return `${sessionTypeTitle} (${getProgramApplicationLabel(programApplication)})`;
    }
    throw new Error('Unknown coach session opportunity type');
}

export default class CreateOrUpdateCoachSessionOpportunity extends AbstractUserManagementAction {
    static namespace = UserManagementActionNamespace.User;
    static actionType = UserActionType.CreateOrUpdateCoachSessionOpportunity;

    static description = (<p>Create or update a coach session opportunity for the user.</p>);

    static formValidationSchema = Yup.object().shape({
        sessionType: Yup.string().required('Session type is required'),
        programApplicationId: Yup.string().when('sessionType', {
            is: 'post_application_goal_setting',
            then: Yup.string().required('Program application is required for this session type'),
            otherwise: Yup.string(),
        }),
        opportunityId: Yup.string(),
        expired: Yup.boolean(),
    });

    static FormFields = ({ userId }: FormFieldProps<never, CreateOrUpdateCoachSessionOpportunity>) => {
        const { user } = useGetUserInAdmin(userId);
        const { watch, reset } = useFormContext<FormFields>();
        const sessionType = watch('sessionType');
        const opportunityId = watch('opportunityId');
        const programApplications = user!.programApplications;

        // Cannot update complete opportunities
        const coachSessionOpportunities = user!.coachSessionOpportunities.filter(o => !o.complete);

        const coachSessionOpportunity = useMemo(
            () => coachSessionOpportunities?.find(o => o.id === opportunityId) || null,
            [coachSessionOpportunities, opportunityId],
        );

        useEffect(() => {
            let programApplicationId;
            if (coachSessionOpportunity) {
                programApplicationId = coachSessionOpportunity.programApplicationId;
            } else if (sessionType === 'post_application_goal_setting' && programApplications?.length === 1) {
                programApplicationId = programApplications[0].id;
            }

            reset({
                opportunityId: coachSessionOpportunity?.id || 'new',
                sessionType: coachSessionOpportunity?.sessionType || defaultSessionType,
                programApplicationId,
                expired: coachSessionOpportunity?.expired || false,
            });
        }, [reset, coachSessionOpportunity, programApplications, sessionType]);

        const programApplicationOptions = useMemo(() => {
            const options = programApplications?.map(app => ({
                value: app.id,
                label: getProgramApplicationLabel(app),
            }));
            return options || [];
        }, [programApplications]);

        const coachSessionOpportunityOptions = useMemo(
            () =>
                [{ value: 'new', label: '-- Create New Opportunity --' }].concat(
                    coachSessionOpportunities.map(opportunity => ({
                        value: opportunity.id,
                        label: getCoachSessionOpportunityLabel(opportunity, programApplications),
                    })),
                ),
            [coachSessionOpportunities, programApplications],
        );

        return (
            <>
                <Select
                    label="Coaching Session Opportunity"
                    name="opportunityId"
                    disabled={coachSessionOpportunities?.length === 0}
                    options={coachSessionOpportunityOptions}
                />

                <Select
                    label="Session Type"
                    name="sessionType"
                    disabled={availableSessionTypes.length < 2 || !!coachSessionOpportunity}
                    options={availableSessionTypes.map(type => ({
                        value: type,
                        label: titleize(humanize(type)),
                    }))}
                />

                {sessionType === 'post_application_goal_setting' && programApplicationOptions.length > 0 && (
                    <Select
                        disabled={programApplicationOptions.length === 1}
                        label="Program Application"
                        name="programApplicationId"
                        options={programApplicationOptions}
                    />
                )}

                <Checkbox label="Mark as Expired" name="expired" disabled={!coachSessionOpportunity} />
            </>
        );
    };
}
